import React, { useState, useEffect } from "react";
import { S } from "../../crm/theme";
import { fetchPnlSettings, savePnlSettings, fetchPnlEntriesV2, COMMON_PROJECT } from "../../crm/crmApi";
import PnlCopyOld from "./PnlCopyOld";

// P&L Settings tab (only the two Owners ever see this; the database rule says the same).
// 1) The list of projects used by the P&L. Add a project, or hide one.
//    Hiding never deletes anything: old entries keep their project name.
// 2) Which Google Cloud project belongs to which app (used later to split the Google bill).

const NAME_OK = /^[A-Za-z0-9][A-Za-z0-9 &().-]*$/;
const GOOGLE_OK = /^[a-z][a-z0-9-]{3,39}$/;

export default function PnlSettings({ user }) {
  const [settings, setSettings] = useState(null);
  const [msgObj, setMsgObj] = useState({ text: "", where: "top" });
  const say = (text, where) => setMsgObj({ text, where: where || "top" });
  const [busy, setBusy] = useState(false);
  const [newName, setNewName] = useState("");
  const [newGoogle, setNewGoogle] = useState("");
  const [newApp, setNewApp] = useState("");

  const load = async () => {
    try {
      const s = await fetchPnlSettings();
      setSettings(s);
    } catch (e) {
      say("Could not load settings: " + e.message);
    }
  };
  useEffect(() => { load(); }, []);

  // How many entries each project has (null = could not be read, then Delete stays off).
  const [usage, setUsage] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const list = await fetchPnlEntriesV2();
        const u = {};
        list.forEach((e) => { if (e.project) u[e.project] = (u[e.project] || 0) + 1; });
        setUsage(u);
      } catch (e) { setUsage(null); }
    })();
  }, []);

  // Saves the whole list, then shows the saved version on screen.
  const persist = async (next, okText, where) => {
    setBusy(true);
    try {
      await savePnlSettings(next, user);
      setSettings({ ...next, saved: true });
      say(okText, where);
    } catch (e) {
      say("Save failed: " + e.message, where);
    }
    setBusy(false);
  };

  if (!settings) {
    return <div style={{ color: "#7b82a0" }}>{msgObj.text || "Loading…"}</div>;
  }

  const visibleNames = settings.projects.filter((p) => !p.hidden).map((p) => p.name);

  const addProject = async () => {
    const name = newName.trim().replace(/\s+/g, " ");
    if (name.length < 2 || name.length > 40) { say("Project name must be 2 to 40 characters.", "project"); return; }
    if (!NAME_OK.test(name)) { say("Use only letters, numbers, spaces and & ( ) . - in the name.", "project"); return; }
    if (settings.projects.some((p) => p.name.toLowerCase() === name.toLowerCase())) {
      say("A project with this name already exists (it may be hidden). Use Show to bring it back.", "project");
      return;
    }
    // New projects go before "Company (common)", which stays last.
    const rest = settings.projects.filter((p) => p.name !== COMMON_PROJECT);
    const common = settings.projects.filter((p) => p.name === COMMON_PROJECT);
    await persist({ ...settings, projects: [...rest, { name, hidden: false }, ...common] }, `Project "${name}" added.`, "project");
    setNewName("");
  };

  const toggleHide = async (name) => {
    if (name === COMMON_PROJECT) return;
    const p = settings.projects.find((x) => x.name === name);
    if (!p) return;
    if (!p.hidden && !window.confirm(`Hide "${name}"? It will disappear from the dropdown lists. Old entries keep the name and nothing is deleted. You can show it again later.`)) return;
    const projects = settings.projects.map((x) => (x.name === name ? { ...x, hidden: !x.hidden } : x));
    await persist({ ...settings, projects }, p.hidden ? `"${name}" is shown again.` : `"${name}" is hidden.`, "project");
  };

  // Delete is only for a project that has no entries at all and no Google link.
  const canDelete = (name) =>
    name !== COMMON_PROJECT && usage !== null && !(usage[name] > 0) && !settings.googleLinks.some((l) => l.app === name);

  const deleteProject = async (name) => {
    if (!canDelete(name)) return;
    if (!window.confirm(`Delete "${name}" for good? It has no entries, so nothing else is affected.`)) return;
    await persist({ ...settings, projects: settings.projects.filter((x) => x.name !== name) }, `Project "${name}" deleted.`, "project");
  };

  const addLink = async () => {
    const g = newGoogle.trim().toLowerCase();
    if (!GOOGLE_OK.test(g)) { say("Type the Google project ID exactly as in Firebase (small letters, numbers and dashes, for example abc-saas-prod).", "link"); return; }
    if (!newApp) { say("Choose the app this Google project belongs to.", "link"); return; }
    if (settings.googleLinks.some((l) => l.googleProject === g)) { say("This Google project is already in the list.", "link"); return; }
    await persist({ ...settings, googleLinks: [...settings.googleLinks, { googleProject: g, app: newApp }] }, `Google project "${g}" linked to ${newApp}.`, "link");
    setNewGoogle("");
  };

  const removeLink = async (g) => {
    if (!window.confirm(`Remove the link for "${g}"? No money entry is affected.`)) return;
    await persist({ ...settings, googleLinks: settings.googleLinks.filter((l) => l.googleProject !== g) }, `Link for "${g}" removed.`, "link");
  };

  const changeLinkApp = async (g, app) => {
    await persist({ ...settings, googleLinks: settings.googleLinks.map((l) => (l.googleProject === g ? { ...l, app } : l)) }, `"${g}" now belongs to ${app}.`, "link");
  };

  return (
    <div>
      {msgObj.text && msgObj.where === "top" && <div style={S.msg}>{msgObj.text}</div>}

      {!settings.saved && (
        <div style={{ ...S.card, marginBottom: 16 }}>
          <div style={{ fontSize: 13, color: "#f0b429", marginBottom: 10 }}>
            This is the starting list. It is not saved yet. Press the button once to save it.
          </div>
          <button style={S.btnBlue} disabled={busy} onClick={() => persist(settings, "Starting list saved.")}>
            {busy ? "Saving…" : "Save the starting list"}
          </button>
        </div>
      )}

      <div style={S.section}>Projects</div>
      <div style={S.tableWrap}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr><th style={S.th}>Project</th><th style={S.th}>Status</th><th style={S.th}></th></tr>
          </thead>
          <tbody>
            {settings.projects.map((p) => (
              <tr key={p.name} style={p.hidden ? { opacity: 0.55 } : undefined}>
                <td style={S.td}>{p.name}</td>
                <td style={S.td}>
                  {p.name === COMMON_PROJECT ? "Always on (whole business)" : p.hidden ? "Hidden" : "In use"}
                </td>
                <td style={S.td}>
                  {p.name !== COMMON_PROJECT && (
                    <>
                      <button style={S.btnSmall} disabled={busy} onClick={() => toggleHide(p.name)}>
                        {p.hidden ? "Show" : "Hide"}
                      </button>
                      {canDelete(p.name) && (
                        <button style={{ ...S.btnRed, marginLeft: 8 }} disabled={busy} onClick={() => deleteProject(p.name)}>
                          Delete
                        </button>
                      )}
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={S.form}>
        <label style={S.label}>Add a new project</label>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <input
            style={{ ...S.input, maxWidth: 320 }}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Project name, for example Society App 2"
          />
          <button style={S.btnBlue} disabled={busy} onClick={addProject}>Add project</button>
        </div>
        {msgObj.text && msgObj.where === "project" && <div style={{ ...S.msg, marginTop: 12, marginBottom: 0 }}>{msgObj.text}</div>}
      </div>

      <div style={S.section}>Google Cloud projects and their apps</div>
      <div style={{ color: "#7b82a0", fontSize: 13, marginBottom: 10 }}>
        When you enter a Google Cloud bill, the cost is split by these links.
      </div>
      <div style={S.tableWrap}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr><th style={S.th}>Google project</th><th style={S.th}>Belongs to app</th><th style={S.th}></th></tr>
          </thead>
          <tbody>
            {settings.googleLinks.length === 0 && (
              <tr><td style={S.td} colSpan={3}>No Google projects linked yet.</td></tr>
            )}
            {settings.googleLinks.map((l) => (
              <tr key={l.googleProject}>
                <td style={{ ...S.td, fontFamily: "monospace" }}>{l.googleProject}</td>
                <td style={S.td}>
                  <select style={{ ...S.select, maxWidth: 240 }} value={l.app} disabled={busy} onChange={(e) => changeLinkApp(l.googleProject, e.target.value)}>
                    {!visibleNames.includes(l.app) && <option value={l.app}>{l.app} (hidden)</option>}
                    {visibleNames.map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                </td>
                <td style={S.td}>
                  <button style={S.btnRed} disabled={busy} onClick={() => removeLink(l.googleProject)}>Remove link</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={S.form}>
        <label style={S.label}>Link a Google project to an app</label>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <input
            style={{ ...S.input, maxWidth: 260 }}
            value={newGoogle}
            onChange={(e) => setNewGoogle(e.target.value)}
            placeholder="Google project ID, e.g. abc-saas-prod"
          />
          <select style={{ ...S.select, maxWidth: 220 }} value={newApp} onChange={(e) => setNewApp(e.target.value)}>
            <option value="">Choose the app…</option>
            {visibleNames.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          <button style={S.btnBlue} disabled={busy} onClick={addLink}>Add link</button>
        </div>
        {msgObj.text && msgObj.where === "link" && <div style={{ ...S.msg, marginTop: 12, marginBottom: 0 }}>{msgObj.text}</div>}
      </div>

      <PnlCopyOld user={user} />
    </div>
  );
}
